/**
 * Unsubscribe Page
 *
 * Public page for email unsubscribe confirmation.
 * Clean, simple, one-click confirmation design.
 */
import { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Button, Card, CardContent, Skeleton } from '@respectly/ui';
import { MailX, CheckCircle2, AlertCircle, Shield, ArrowLeft } from 'lucide-react';

interface UnsubscribeInfo {
  contactEmail?: string;
  contactFirstName?: string;
  tenantName: string;
  alreadyUnsubscribed: boolean;
}

type PageState = 'loading' | 'confirm' | 'success' | 'already' | 'error' | 'expired';

export function Unsubscribe() {
  const [state, setState] = useState<PageState>('loading');
  const [info, setInfo] = useState<UnsubscribeInfo | null>(null);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [isProcessing, setIsProcessing] = useState(false);

  // Get token from URL
  const token = window.location.pathname.split('/unsubscribe/')[1];
  const apiBase = import.meta.env.VITE_API_URL || 'http://localhost:3000';

  const fetchUnsubscribeInfo = useCallback(async () => {
    try {
      const response = await fetch(`${apiBase}/api/unsubscribe/${token}`);
      const data = await response.json();

      if (!response.ok || !data.success) {
        if (data.error?.message?.includes('expired')) {
          setState('expired');
          setErrorMessage('This unsubscribe link has expired.');
        } else {
          setState('error');
          setErrorMessage(data.error?.message || 'Unable to process unsubscribe request');
        }
        return;
      }

      setInfo(data.data);

      if (data.data.alreadyUnsubscribed) {
        setState('already');
      } else {
        setState('confirm');
      }
    } catch (error) {
      console.error('Error fetching unsubscribe info:', error);
      setState('error');
      setErrorMessage('Unable to connect to server. Please try again.');
    }
  }, [apiBase, token]);

  useEffect(() => {
    if (!token || token.length < 32) {
      setState('expired');
      setErrorMessage('Invalid unsubscribe link');
      return;
    }

    // Fetch unsubscribe info
    fetchUnsubscribeInfo();
  }, [token, fetchUnsubscribeInfo]);

  const handleUnsubscribe = async () => {
    setIsProcessing(true);

    try {
      const response = await fetch(`${apiBase}/api/unsubscribe/${token}`, {
        method: 'POST',
      });
      const data = await response.json();

      if (!response.ok || !data.success) {
        setState('error');
        setErrorMessage(data.error?.message || 'Unable to process unsubscribe request');
        return;
      }

      setState('success');
    } catch (error) {
      console.error('Error processing unsubscribe:', error);
      setState('error');
      setErrorMessage('Unable to process your request. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  // Loading state
  if (state === 'loading') {
    return (
      <UnsubscribeLayout>
        <Card>
          <CardContent className="p-8">
            <div className="flex flex-col items-center">
              <Skeleton className="h-16 w-16 rounded-full mb-4" />
              <Skeleton className="h-6 w-48 mb-2" />
              <Skeleton className="h-4 w-64" />
            </div>
          </CardContent>
        </Card>
      </UnsubscribeLayout>
    );
  }

  // Confirmation state
  if (state === 'confirm') {
    return (
      <UnsubscribeLayout>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          <Card>
            <CardContent className="p-8 text-center">
              <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-orange-100">
                <MailX className="h-8 w-8 text-orange-600" />
              </div>

              <h1 className="text-2xl font-semibold text-gray-900 mb-2">
                Unsubscribe from Emails
              </h1>

              {info?.contactFirstName && (
                <p className="text-gray-600 mb-1">
                  Hi {info.contactFirstName},
                </p>
              )}

              <p className="text-gray-600 mb-6">
                You are about to unsubscribe from emails from{' '}
                <span className="font-medium text-gray-900">{info?.tenantName}</span>.
              </p>

              {info?.contactEmail && (
                <div className="bg-gray-50 rounded-lg p-4 mb-6">
                  <p className="text-sm text-gray-500">Email address</p>
                  <p className="font-mono text-gray-900">{info.contactEmail}</p>
                </div>
              )}

              <div className="space-y-3">
                <Button
                  onClick={handleUnsubscribe}
                  disabled={isProcessing}
                  className="w-full"
                  size="lg"
                  variant="destructive"
                >
                  {isProcessing ? 'Processing...' : 'Unsubscribe'}
                </Button>

                <p className="text-xs text-gray-500">
                  You will no longer receive marketing emails. Transactional emails may still be sent.
                </p>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </UnsubscribeLayout>
    );
  }

  // Success state
  if (state === 'success') {
    return (
      <UnsubscribeLayout>
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.3 }}
        >
          <Card>
            <CardContent className="p-8 text-center">
              <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-green-100">
                <CheckCircle2 className="h-8 w-8 text-green-600" />
              </div>

              <h1 className="text-2xl font-semibold text-gray-900 mb-2">
                Successfully Unsubscribed
              </h1>

              <p className="text-gray-600 mb-6">
                You have been removed from the mailing list for{' '}
                <span className="font-medium text-gray-900">{info?.tenantName}</span>.
              </p>

              <div className="bg-gray-50 rounded-lg p-4 mb-6 text-left">
                <h3 className="font-medium text-gray-900 mb-2">What happens now?</h3>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>You will no longer receive marketing emails</li>
                  <li>Important transactional emails may still be sent</li>
                  <li>You can resubscribe anytime by contacting us</li>
                </ul>
              </div>

              <Button
                variant="outline"
                onClick={() => window.close()}
                className="w-full"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Close this page
              </Button>
            </CardContent>
          </Card>
        </motion.div>
      </UnsubscribeLayout>
    );
  }

  // Already unsubscribed state
  if (state === 'already') {
    return (
      <UnsubscribeLayout>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          <Card>
            <CardContent className="p-8 text-center">
              <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-blue-100">
                <CheckCircle2 className="h-8 w-8 text-blue-600" />
              </div>

              <h1 className="text-2xl font-semibold text-gray-900 mb-2">
                Already Unsubscribed
              </h1>

              <p className="text-gray-600 mb-6">
                You have already been unsubscribed from emails from{' '}
                <span className="font-medium text-gray-900">{info?.tenantName}</span>.
              </p>

              <p className="text-sm text-gray-500">
                No further action is needed.
              </p>
            </CardContent>
          </Card>
        </motion.div>
      </UnsubscribeLayout>
    );
  }

  // Error or expired state
  return (
    <UnsubscribeLayout>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        <Card>
          <CardContent className="p-8 text-center">
            <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-red-100">
              <AlertCircle className="h-8 w-8 text-red-600" />
            </div>

            <h1 className="text-2xl font-semibold text-gray-900 mb-2">
              {state === 'expired' ? 'Link Expired' : 'Unable to Unsubscribe'}
            </h1>

            <p className="text-gray-600 mb-6">
              {errorMessage || 'Something went wrong. Please try again.'}
            </p>

            <div className="bg-gray-50 rounded-lg p-4 text-left">
              <h3 className="font-medium text-gray-900 mb-2">Need help?</h3>
              <p className="text-sm text-gray-600">
                If you continue to receive unwanted emails, please contact the sender directly
                or reply to the email with "UNSUBSCRIBE" in the subject line.
              </p>
            </div>
          </CardContent>
        </Card>
      </motion.div>
    </UnsubscribeLayout>
  );
}

// Layout wrapper component
function UnsubscribeLayout({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen flex flex-col bg-slate-50">
      {/* Header */}
      <header className="border-b bg-white">
        <div className="container mx-auto flex h-14 items-center px-4">
          <div className="flex items-center gap-2">
            <div className="h-8 w-8 rounded-lg bg-primary-500" />
            <span className="font-semibold">Respectly</span>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex flex-1 items-center justify-center p-4">
        <div className="w-full max-w-md">
          {children}

          {/* Security Badge */}
          <div className="mt-6 flex items-center justify-center gap-2 text-sm text-muted-foreground">
            <Shield className="h-4 w-4" />
            <span>Secure & encrypted</span>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t bg-white py-4">
        <div className="container mx-auto px-4 text-center text-xs text-muted-foreground">
          <p>Powered by Respectly</p>
        </div>
      </footer>
    </div>
  );
}

export default Unsubscribe;
